import { z } from "zod";
import { schemes } from "../../schema-validation";
import { worker_types } from "../../workers-shared";
import { EGovernmentPhotoIDType } from "../../accounts";
import { EPaycardSSNVerificationStageID } from "../PaycardSSNVerificationHistory";
import { paycardSSNVerificationDeclinedReasonCodeSchema } from "../PaycardSSNVerificationDeclinedReasonCodes";
import { ESSNVerificationStatus } from "../../kyc";

const baseSchema = z.object({
  _id: schemes.mongoObjectId(),
  worker_id: z.string(),
  worker_type: z.nativeEnum(worker_types),
  company_id: z.string(),

  createdAt: z.date(),
  finished_at: z.date(),
  updatedAt: z.date(),
});

const ssnVerificationStatusBaseSchema = z.object({
  ssn_verification_error: z.string().optional(),
  ssn_verification_failed_reason: z.string().optional(),
  ssn_verification_failed_validation: z.string().optional(),
  ssn_verification_status: z.nativeEnum(ESSNVerificationStatus),
});

const profileFieldsSchema = z
  .array(
    z.object({
      field_name: z.string(),
      new_value: z.string().optional(),
      old_value: z.string().optional(),
    })
  )
  .optional();

export type TPaycardSSNVerificationHistoryProfileField = z.infer<
  typeof profileFieldsSchema
>;

export const paycardSSNVerificationHistorySchema = z.discriminatedUnion(
  "stage_id",
  [
    z
      .object({
        stage_id: z.literal(EPaycardSSNVerificationStageID.initial),
      })
      .merge(baseSchema)
      .merge(ssnVerificationStatusBaseSchema),
    z
      .object({
        stage_id: z.literal(EPaycardSSNVerificationStageID.id_verification),

        stage_data: z.object({
          profile_fields: profileFieldsSchema,
          document_data: z.object({
            document_key: z.nativeEnum(EGovernmentPhotoIDType),
            veriff_session_id: z.string(),
          }),
        }),
      })
      .merge(baseSchema)
      .merge(ssnVerificationStatusBaseSchema),
    z
      .object({
        stage_id: z.literal(
          EPaycardSSNVerificationStageID.id_verification_internal_review
        ),

        declined_reason_codes: z.array(
          paycardSSNVerificationDeclinedReasonCodeSchema
        ),
        declined_reason_description: z
          .string()
          .max(500, {
            message: "Please limit the description to under 500 characters.",
          })
          .optional(),
      })
      .merge(baseSchema),
    z
      .object({
        stage_id: z.literal(
          EPaycardSSNVerificationStageID.social_security_card_verification
        ),

        stage_data: z.object({
          profile_fields: profileFieldsSchema.optional(),
          document_data: z.object({
            document_key: z.literal("social_security_card"),
            persona_inquiry_id: z.string(),
          }),
        }),
      })
      .merge(baseSchema)
      .merge(ssnVerificationStatusBaseSchema),
    z
      .object({
        stage_id: z.literal(
          EPaycardSSNVerificationStageID.social_security_card_internal_review
        ),
      })
      .merge(baseSchema)
      .merge(ssnVerificationStatusBaseSchema),
    z
      .object({
        stage_id: z.literal(EPaycardSSNVerificationStageID.internal_decision),
        ssn_verification_status: z.enum([
          ESSNVerificationStatus.compliance_approved,
          ESSNVerificationStatus.compliance_declined,
          ESSNVerificationStatus.declined,
        ]),
        internal_remarks: z
          .string()
          .max(500, {
            message: "Please limit the remarks to under 500 characters.",
          })
          .optional(),
        archived: z.boolean().optional(),
        archived_reason: z
          .string()
          .max(500, {
            message: "Please limit the reason to under 500 characters.",
          })
          .optional(),
      })
      .merge(baseSchema)
      .merge(ssnVerificationStatusBaseSchema),
  ]
);
